/**
 * Role-based home paths.
 *
 * When someone signs in they are sent straight to the screen their job
 * actually starts on: a cleaner to today's jobs, a chef to production, a
 * storekeeper to the store, an MD to the control tower.
 *
 * This matters more than it looks. The fallback is `/dashboard`, which is a
 * company revenue-and-margin screen — the wrong landing page for a frontline
 * worker on a phone, and one most roles are not entitled to read anyway. Any
 * role missing from the map below silently lands there, so every role the
 * business actually uses is listed explicitly.
 *
 * Kept in one file so the login page and any guard redirect share the same
 * mapping — a mismatch causes hard-to-diagnose "why did I land back on
 * /dashboard" reports.
 */

const ROLE_HOME: Record<string, string> = {
  /* Management — the consolidated business position. */
  MD: '/management/control-tower',
  VP: '/management/control-tower',
  GM: '/management/control-tower',

  /* Operations line — sites they are responsible for. */
  'Operations Head': '/sites',
  'Regional Manager': '/sites',
  'Project Manager': '/sites',
  'Site Manager': '/sites',

  /* Frontline — today's work, nothing else. Built for a phone. */
  Supervisor: '/my-work',
  Chef: '/kitchen/production',
  'Kitchen Staff': '/my-work',
  Storekeeper: '/inventory/stock',
  'Service Staff': '/my-work',
  Cleaner: '/my-work',

  /* Departments — each opens on the board they own. */
  'HR Manager': '/hr/dashboard',
  'Finance Manager': '/billing/dashboard',
  Accountant: '/accounting/dashboard',
  'Procurement Manager': '/purchase/orders',
  'Tender Manager': '/tenders/dashboard',
  'Events Manager': '/events',
  'Quality Manager': '/quality/dashboard',
  'Maintenance Manager': '/maintenance/requests',

  /* Legacy platform roles, kept working for the older demo logins. */
  Owner: '/management/control-tower',
  Manager: '/sites',
  Salesperson: '/billing/invoices',
  Employee: '/my-work',
};

/**
 * Priority when a user holds more than one role: the most senior wins, so an
 * MD who is also seeded as an Employee is not quarantined to /my-work.
 * Ordered most senior first.
 */
const PRIORITY: readonly string[] = [
  'MD',
  'VP',
  'GM',
  'Owner',
  'Operations Head',
  'Regional Manager',
  'Project Manager',
  'Finance Manager',
  'HR Manager',
  'Procurement Manager',
  'Tender Manager',
  'Events Manager',
  'Quality Manager',
  'Maintenance Manager',
  'Accountant',
  'Manager',
  'Site Manager',
  'Supervisor',
  'Chef',
  'Storekeeper',
  'Salesperson',
  'Service Staff',
  'Kitchen Staff',
  'Cleaner',
  'Employee',
];

/**
 * The path a user with these roles should land on after signing in.
 * Falls back to `/dashboard` only when no known role matches.
 */
export function homePathForRoles(roles: readonly string[] | undefined | null): string {
  if (!roles || roles.length === 0) return '/dashboard';

  for (const role of PRIORITY) {
    if (roles.includes(role)) return ROLE_HOME[role] ?? '/dashboard';
  }

  // An unknown role still gets a working screen rather than a KPI page it
  // may not be allowed to load.
  return '/my-work';
}
